import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, type VerificationChannel, type User as UserType } from '@/context/AuthContext';
import { validateForm, backendRegistrationSchema, validatePassword, nameSchema } from '../utils/validationUtils';
import { useEmailValidation } from '../hooks/useEmailValidation';
import { isSMSVerificationRequired } from '../config/features';
import Logo from './Logo';
import Navigation from './Navigation';
import { apiUrl } from '@/config/api';
import { dataPreloader } from '@/services/dataPreloader';
import { z } from 'zod';
import { 
  User, 
  Mail, 
  Lock, 
  Phone, 
  Building,
  Check,
  ChevronRight,
  Eye,
  EyeOff,
  Loader2,
  ArrowLeft,
  Users,
  Scissors,
  Info,
  X,
  Crown
} from 'lucide-react';
import InternationalPhoneInput from './common/InternationalPhoneInput';
import SubscriptionPlanSelector from './SubscriptionPlanSelector';

interface RegistrationData {
  // Required Fields
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  phone: string;
  userType: 'customer' | 'business';
  selectedPlan?: string; // Required for business, optional for customer (defaults to 'free')
  
  // Optional Fields
  businessName?: string;
  businessType?: 'tailor' | 'fashion_house' | 'boutique';
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  serviceAreas?: string[];
  specialties?: string[];
  preferences?: {
    notifications: boolean;
    marketing: boolean;
    language: string;
  };
  socialLinks?: {
    website?: string;
    instagram?: string;
    facebook?: string;
  };
  smsConsent?: boolean;
}

interface LoginData {
  email: string;
  password: string;
}

const UnifiedAuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    login,
    register,
    user,
    verificationState,
    requestVerification,
    verifyCode,
    dismissVerificationPrompt,
    resetVerificationError,
  } = useAuth();
  
  // Auth mode: 'signin' | 'signup'
  const modeFromUrl = searchParams.get('mode') as 'signin' | 'signup' | null;
  const isOAuthFlow = searchParams.get('oauth') === 'true';
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>(modeFromUrl || 'signin');
  
  // Sync authMode with URL parameter changes
  useEffect(() => {
    if (modeFromUrl && modeFromUrl !== authMode) {
      setAuthMode(modeFromUrl);
      setError('');
      setCurrentStep(1);
      // Reset form data when switching modes
      if (modeFromUrl === 'signup') {
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          fullName: '',
          phone: '',
          userType: 'customer',
          selectedPlan: 'free',
          businessName: '',
          businessType: 'tailor',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'India'
          },
          serviceAreas: [],
          specialties: [],
          preferences: {
            notifications: true,
            marketing: false,
            language: 'en'
          },
          socialLinks: {
            website: '',
            instagram: '',
            facebook: ''
          },
          smsConsent: false
        });
        // Reset email verification state
        setEmailVerified(false);
        setOtpSent(false);
        setOtpCode('');
        setOtpError(null);
        if (resendTimer) {
          clearInterval(resendTimer);
          setResendTimer(null);
        }
        setResendCooldown(0);
      } else {
        setLoginData({
          email: '',
          password: ''
        });
      }
    }
  }, [modeFromUrl]);
  
  // Pre-fill form data from OAuth if available and validate OAuth status
  useEffect(() => {
    if (isOAuthFlow && authMode === 'signup') {
      // Check OAuth authentication status early
      const token = localStorage.getItem('authToken');
      const userDataStr = localStorage.getItem('user');
      
      if (!token) {
        // No token means OAuth didn't complete successfully
        setError('OAuth authentication error. Please try signing in again.');
        // Reset to step 1 to show error at the beginning
        setCurrentStep(1);
        return;
      }

      // Try to parse user data from localStorage
      let userData: any = null;
      if (userDataStr) {
        try {
          userData = JSON.parse(userDataStr);
        } catch (_e) {
          // Ignore parse errors
        }
      }

      // Check if user has a valid role for OAuth completion
      const userRole = userData?.role as string || '';
      if (userRole !== 'pending' && userRole !== 'customer' && userRole !== 'tailor' && userRole !== 'admin') {
        // User doesn't have a valid role for OAuth completion
        setError('OAuth authentication error. Please try signing in again.');
        // Reset to step 1 to show error at the beginning
        setCurrentStep(1);
        return;
      }

      // Pre-fill form data from OAuth
      const oauthData = localStorage.getItem('oauthUserData');
      if (oauthData) {
        try {
          const parsed = JSON.parse(oauthData);
          setFormData(prev => ({
            ...prev,
            email: parsed.email || prev.email,
            fullName: parsed.name || prev.fullName
          }));
          // Clear OAuth data after using it
          localStorage.removeItem('oauthUserData');
        } catch (_e) {
          /* Ignore parse errors */
        }
      } else if (userData) {
        // Use user data from localStorage if oauthUserData is not available
        setFormData(prev => ({
          ...prev,
          email: userData.email || prev.email,
          fullName: userData.name || prev.fullName
        }));
      }
    }
  }, [isOAuthFlow, authMode, navigate]);
  
  // Registration state
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<RegistrationData>({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
    userType: 'customer',
    selectedPlan: 'free', // Default to free plan
    businessName: '',
    businessType: 'tailor',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'India'
    },
    serviceAreas: [],
    specialties: [],
    preferences: {
      notifications: true,
      marketing: false,
      language: 'en'
    },
    socialLinks: {
      website: '',
      instagram: '',
      facebook: ''
    },
    smsConsent: false
  });
  
  // Login state
  const [loginData, setLoginData] = useState<LoginData>({
    email: '',
    password: ''
  });
  
  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  
  // Regex patterns for password validation
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedVerificationChannel, setSelectedVerificationChannel] = useState<VerificationChannel>('email');
  const [verificationCodeInput, setVerificationCodeInput] = useState('');
  const [verificationFeedback, setVerificationFeedback] = useState<string | null>(null);
  
  // Email OTP verification state for registration
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState<Date | null>(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds remaining
  const [resendTimer, setResendTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Email validation for registration
  const emailValidation = useEmailValidation(formData.email);
  
  // Get role from URL params
  const roleFromUrl = searchParams.get('role') as 'customer' | 'business' | null;
  
  useEffect(() => {
    if (roleFromUrl) {
      setFormData(prev => ({ 
        ...prev, 
        userType: roleFromUrl,
        selectedPlan: roleFromUrl === 'business' ? (prev.selectedPlan || 'free') : 'free'
      }));
    }
  }, [roleFromUrl]);

  useEffect(() => {
    if (verificationState.channel) {
      setSelectedVerificationChannel(verificationState.channel);
    } else if (verificationState.showPrompt) {
      if (user?.email) {
        setSelectedVerificationChannel('email');
      } else if (user?.phone) {
        setSelectedVerificationChannel('sms');
      }
    }
  }, [verificationState.channel, verificationState.showPrompt, user]);

  useEffect(() => {
    if (verificationState.showPrompt) {
      setVerificationCodeInput('');
      setVerificationFeedback(null);
      resetVerificationError();
    }
  }, [verificationState.showPrompt, resetVerificationError]);

  // Reset OTP state when email changes
  useEffect(() => {
    if (currentStep === 2 && !isOAuthFlow && formData.email) {
      // Check if email was verified for this specific email
      const verified = localStorage.getItem(`email_verified_${formData.email}`);
      if (verified !== 'true') {
        // Email changed or not verified, reset state
        setEmailVerified(false);
        setOtpSent(false);
        setOtpCode('');
        setOtpError(null);
        setResendCooldown(0);
        if (resendTimer) {
          clearInterval(resendTimer);
          setResendTimer(null);
        }
      }
    }
  }, [formData.email, currentStep, isOAuthFlow]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setResendTimer(timer);
      return () => clearInterval(timer);
    } else if (resendTimer) {
      clearInterval(resendTimer);
      setResendTimer(null);
    }
  }, [resendCooldown]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (resendTimer) {
        clearInterval(resendTimer);
      }
    };
  }, [resendTimer]);

  // Send OTP for email verification
  const handleSendRegistrationOtp = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      setOtpError('Please enter a valid email address');
      return;
    }

    setIsSendingOtp(true);
    setOtpError(null);

    try {
      const response = await fetch(apiUrl('/api/register/send-otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          user_name: formData.fullName || 'User'
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOtpSent(true);
        setOtpCode('');
        if (data.expires_at) {
          setOtpExpiresAt(new Date(data.expires_at));
        }
        setResendCooldown(120); // 2 minutes in seconds
        setOtpError(null);
      } else {
        setOtpError(data.detail || data.error || 'Failed to send verification code');
      }
    } catch (_error) {
      setOtpError('Failed to send verification code. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP
  const handleVerifyRegistrationOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter a 6-digit verification code');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const response = await fetch(apiUrl('/api/register/verify-otp'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          otp_code: otpCode
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setEmailVerified(true);
        setOtpError(null);
        // Store verification in localStorage to persist across steps
        localStorage.setItem(`email_verified_${formData.email}`, 'true');
      } else {
        setOtpError(data.detail || data.error || 'Invalid verification code');
      }
    } catch (_error) {
      setOtpError('Failed to verify code. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Check if email was already verified (from localStorage)
  useEffect(() => {
    if (formData.email && !isOAuthFlow) {
      const verified = localStorage.getItem(`email_verified_${formData.email}`);
      if (verified === 'true') {
        setEmailVerified(true);
        setOtpSent(true);
      }
    }
  }, [formData.email, isOAuthFlow]);

  const handleGoogleLogin = async () => {
    setSocialLoading('google');
    setError('');

    try {
      const oauthUrl = apiUrl('/auth/google/login');
      
      const popupWindow = window.open(
        oauthUrl,
        'oauth_popup',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popupWindow) {
        // Popup blocked — fall back to full-page OAuth; backend redirects to /auth/oauth-callback
        window.location.href = oauthUrl;
        return;
      }

      const handleMessage = async (event: MessageEvent) => {
        // Allow messages from backend origin (OAuth callback) or same origin
        const backendOrigin = apiUrl('').replace(/\/+$/, '');
        const allowedOrigins = [window.location.origin, backendOrigin];
        
        // Validate message structure first
        if (!event.data || !event.data.type) {
          return;
        }
        
        // Accept messages from allowed origins or if message type matches OAuth types
        // Note: When postMessage uses '*', event.origin will be the sender's origin (backend)
        const isOAuthMessage = event.data.type === 'OAUTH_SUCCESS' || event.data.type === 'OAUTH_ERROR';
        if (!allowedOrigins.includes(event.origin) && !isOAuthMessage) {
          return;
        }
        
        if (event.data.type === 'OAUTH_SUCCESS') {
          // Try to close popup (may fail due to COOP policy, but popup will close itself)
          try {
            popupWindow.close();
          } catch (_e) {
            // Ignore COOP errors - popup will close itself from callback page
          }
          window.removeEventListener('message', handleMessage);
          setSocialLoading(null);
          
          // Store tokens and user data
          const { access_token, refresh_token, user, redirect_path } = event.data;
          localStorage.setItem('authToken', access_token);
          if (refresh_token) {
            localStorage.setItem('refreshToken', refresh_token);
          }
          
          // Store user data
          const userData = {
            id: user.id.toString(),
            email: user.email,
            name: user.full_name,
            role: user.role,
            tier: user.tier || 'free',
            timezone: user.timezone || 'Asia/Kolkata',
            phone: user.phone_number || undefined,
            emailVerifiedAt: user.email_verified_at || null,
            phoneVerifiedAt: user.phone_verified_at || null,
            preferredVerificationMethod: user.preferred_verification_method || null,
          };
          localStorage.setItem('user', JSON.stringify(userData));
          
          // Check if user needs to complete registration (pending role)
          // Redirect to full registration workflow with email and name pre-filled
          if (user.role === 'pending') {
            // Store OAuth user data temporarily to pre-fill registration form
            localStorage.setItem('oauthUserData', JSON.stringify({
              email: user.email,
              name: user.full_name,
              fromOAuth: true
            }));
            navigate('/register?mode=signup&oauth=true');
            return;
          }
          
          // Navigate based on redirect_path or user role
          const destination = redirect_path || 
            (user.role === 'customer' ? '/customer/dashboard' :
             user.role === 'tailor' ? '/business/dashboard' :
             user.role === 'admin' ? '/admin/dashboard' : '/customer/dashboard');
          navigate(destination);
          
          // Reload page to update auth context
          window.location.reload();
        } else if (event.data.type === 'OAUTH_ERROR') {
          // Try to close popup (may fail due to COOP policy)
          try {
            popupWindow.close();
          } catch (_e) {
            // Ignore COOP errors
          }
          window.removeEventListener('message', handleMessage);
          setSocialLoading(null);
          setError(event.data.error || 'Google login failed.');
        }
      };

      // Cleanup after timeout if no message received (popup might have been closed manually)
      // Note: We can't check window.closed due to COOP policy, so we use a timeout instead
      let cleanupTimeout: NodeJS.Timeout | null = setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        // Only reset loading if still loading (message handler will clear it on success/error)
        setSocialLoading(null);
      }, 300000); // 5 minutes timeout

      // Wrap handleMessage to clear timeout when message is received
      const wrappedHandleMessage = async (event: MessageEvent) => {
        // Process the message first
        await handleMessage(event);
        // Clear timeout after message is processed
        if (cleanupTimeout) {
          clearTimeout(cleanupTimeout);
          cleanupTimeout = null;
        }
      };

      window.addEventListener('message', wrappedHandleMessage);

    } catch (_error) {
      setError('Google login failed. Please try again.');
      setSocialLoading(null);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(loginData.email, loginData.password);
      if (success) {
        // Get user data from localStorage to determine role
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          // Navigate based on user role
          if (user.role === 'customer') {
            navigate('/customer/dashboard');
          } else if (user.role === 'tailor') {
            navigate('/business/dashboard');
          } else if (user.role === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/customer/dashboard'); // Default fallback
          }
        } else {
          navigate('/customer/dashboard'); // Default fallback
        }
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (_error) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const isSMSChannelDisabled = !user?.phone;

  const getContactForChannel = (channel: VerificationChannel): string => {
    if (channel === 'email') {
      return user?.email ?? '';
    }
    return user?.phone ?? '';
  };

  const getMaskedContact = (channel: VerificationChannel): string => {
    const contact = getContactForChannel(channel);
    if (!contact) {
      return '';
    }

    if (channel === 'email') {
      const [localPart, domain] = contact.split('@');
      if (!domain) {
        return contact;
      }
      const visibleLocal = localPart.slice(0, 2);
      return `${visibleLocal}***@${domain}`;
    }

    const digits = contact.replace(/\D/g, '');
    if (digits.length <= 4) {
      return digits.replace(/\d(?=\d{2})/g, '•');
    }
    const maskedSection = '•'.repeat(Math.max(0, digits.length - 4));
    return `${contact.startsWith('+') ? '+' : ''}${maskedSection}${digits.slice(-4)}`;
  };

  const handleVerificationChannelChange = (channel: VerificationChannel) => {
    if (channel === 'sms' && isSMSChannelDisabled) {
      return;
    }
    setSelectedVerificationChannel(channel);
    setVerificationCodeInput('');
    setVerificationFeedback(null);
    resetVerificationError();
  };

  const handleSendVerificationCode = async () => {
    setVerificationFeedback(null);
    resetVerificationError();
    const result = await requestVerification(selectedVerificationChannel);
    if (result.message) {
      setVerificationFeedback(result.message);
    }
    if (result.success) {
      setVerificationCodeInput('');
    }
  };

  const handleVerifyCodeSubmit = async () => {
    if (!verificationCodeInput.trim()) {
      setVerificationFeedback('Please enter the verification code.');
      return;
    }

    resetVerificationError();
    const result = await verifyCode(selectedVerificationChannel, verificationCodeInput.trim());
    if (result.message) {
      setVerificationFeedback(result.message);
    }
    if (result.success) {
      setVerificationCodeInput('');
    }
  };

  const handleDismissVerificationPrompt = () => {
    setVerificationFeedback(null);
    setVerificationCodeInput('');
    resetVerificationError();
    dismissVerificationPrompt();
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentStep < 5) {
      // Validate current step
      const errors = validateStep(currentStep);
      if (Object.keys(errors).length > 0) {
        // Set field-specific errors for better UX
        setFieldErrors(errors);
        // Also set general error for the first error found
        setError(Object.values(errors)[0]);
        return;
      }
      // Clear field errors if validation passes
      setFieldErrors({});
      
      // Check email availability for step 2 (email/password step)
      if (currentStep === 2) {
        if (emailValidation.isAvailable === false) {
          setError('This email is already registered');
          return;
        }
      }
      
      setCurrentStep(currentStep + 1);
      setError('');
    } else {
      // Final submission
      await handleFinalSubmission();
    }
  };

  const handleFinalSubmission = async () => {
    setIsLoading(true);
    setError('');

    try {
      // For OAuth users, use complete-oauth endpoint instead of register
      // Check if it's an OAuth flow - if user exists, complete OAuth registration
      if (isOAuthFlow) {
        // Check OAuth authentication status using localStorage directly
        // (don't rely on user context which might not be loaded yet)
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('Authentication token not found. Please try signing in again.');
        }

        // Get user data from localStorage directly
        const userDataStr = localStorage.getItem('user');
        let userData: any = null;
        if (userDataStr) {
          try {
            userData = JSON.parse(userDataStr);
          } catch (_e) {
            // Ignore parse errors
          }
        }

        // Check if user has a valid role for OAuth completion
        const userRole = userData?.role as string || '';
        if (userRole === 'pending' || userRole === 'customer' || userRole === 'tailor' || userRole === 'admin') {

          const requestData: any = {
            role: formData.userType === 'customer' ? 'customer' : 'tailor',
            full_name: formData.fullName.trim(),
            phone: formData.phone.trim()
          };

          if (formData.userType === 'business') {
            requestData.business_name = formData.businessName?.trim();
            requestData.business_type = formData.businessType || 'tailor';
          }

          const response = await fetch(apiUrl('/api/auth/complete-oauth'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestData)
          });

          const data = await response.json();

          if (!response.ok) {
            let errorMessage = 'Failed to complete registration';
            if (typeof data === 'string') {
              errorMessage = data;
            } else if (data?.detail) {
              errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
            } else if (data?.error) {
              errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
            }
            throw new Error(errorMessage);
          }

          // Update tokens and user data
          if (data.access_token) {
            localStorage.setItem('authToken', data.access_token);
          }
          if (data.refresh_token) {
            localStorage.setItem('refreshToken', data.refresh_token);
          }

          // Update user in localStorage
          const updatedUser = {
            id: data.user.id.toString(),
            email: data.user.email,
            name: data.user.full_name,
            role: data.user.role as 'tailor' | 'customer' | 'admin',
            phone: data.user.phone_number || undefined,
            tier: 'free' as const,
            timezone: 'Asia/Kolkata',
            lastLogin: new Date().toISOString()
          } as UserType;
          localStorage.setItem('user', JSON.stringify(updatedUser));

          // Navigate to appropriate dashboard
          const destination = 
            data.user.role === 'customer' ? '/customer/dashboard' :
            data.user.role === 'tailor' ? '/business/dashboard' :
            '/customer/dashboard';
          
          window.location.href = destination;
          return;
        } else {
          // OAuth flow but user doesn't exist or role is not valid
          // This shouldn't happen if early validation worked, but handle gracefully
          throw new Error('OAuth authentication error. Please try signing in again.');
        }
      }

      // Regular registration flow (non-OAuth)

      // Check email verification (required for non-OAuth users)
      if (!isOAuthFlow && !emailVerified) {
        setError('Please verify your email address before completing registration.');
        setIsLoading(false);
        return;
      }

      // Prepare registration data first
      const registrationPayload = {
        email: formData.email,
        password: formData.password,
        name: formData.fullName,
        role: (formData.userType === 'customer' ? 'customer' : 'tailor') as 'customer' | 'tailor'
      };

      const validation = validateForm(registrationPayload, backendRegistrationSchema);
      
      if (!validation.success) {
        setError(validation.errors[Object.keys(validation.errors)[0]]);
        setIsLoading(false);
        return;
      }

      const registrationSuccess = await register(registrationPayload);
      
      if (registrationSuccess) {
        setError('');
        alert('Registration successful! Please login with your credentials.');
        navigate('/login');
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (error: any) {
      setError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep = (step: number) => {
    const errors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        // Account Type Selection
        if (!formData.userType) {
          errors.userType = 'Please select an account type';
        }
        // Require plan selection for business/tailor accounts
        if (formData.userType === 'business' && !formData.selectedPlan) {
          errors.selectedPlan = 'Please select a subscription plan';
        }
        break;
      case 2:
        // Email/Password (skip password validation for OAuth users)
        if (!formData.email) errors.email = 'Email is required';
        // Require email verification for non-OAuth users
        if (!isOAuthFlow && !emailVerified) {
          errors.emailVerification = 'Please verify your email address before proceeding';
        }
        if (!isOAuthFlow) {
          if (!formData.password) {
            errors.password = 'Password is required';
          } else {
            // Comprehensive password validation using the same schema as final submission
            const passwordValidation = validatePassword(formData.password);
            if (!passwordValidation.isValid) {
              errors.password = passwordValidation.error || 'Password does not meet requirements';
            }
          }
          if (!formData.confirmPassword) {
            errors.confirmPassword = 'Confirm password is required';
          } else if (formData.password && formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
          }
        }
        break;
      case 3:
        // Personal Information
        if (!formData.fullName) {
          errors.fullName = 'Full name is required';
        } else {
          // Validate name format (letters and spaces only)
          try {
            nameSchema.parse(formData.fullName);
          } catch (_error) {
            if (error instanceof z.ZodError) {
              errors.fullName = error.issues[0]?.message || 'Invalid name format';
            }
          }
        }
        if (!formData.phone) errors.phone = 'Phone number is required';
        if (isSMSVerificationRequired() && !formData.smsConsent) {
          errors.smsConsent = 'SMS verification consent is required';
        }
        break;
      case 4:
        // Business Info / Preferences
        if (formData.userType === 'business') {
          if (!formData.businessName) errors.businessName = 'Business name is required';
        }
        break;
    }
    
    return errors;
  };

  const handleInputChange = (field: string, value: any) => {
    if (authMode === 'signin') {
      setLoginData(prev => ({ ...prev, [field]: value }));
      
      // Start preloading data when email is entered (for login)
      if (field === 'email' && value && value.includes('@')) {
        dataPreloader.startPreloading(value);
      }
    } else {
      setFormData(prev => {
        const updated = { ...prev, [field]: value };
        // Auto-set free plan for customers when userType changes
        if (field === 'userType' && value === 'customer') {
          updated.selectedPlan = 'free';
        }
        // Clear selected plan if switching from business to customer
        if (field === 'userType' && value === 'customer' && prev.userType === 'business') {
          updated.selectedPlan = 'free';
        }
        return updated;
      });
    }
  };

  const renderSignInForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Google OAuth Button */}
      <button
        onClick={handleGoogleLogin}
        disabled={socialLoading !== null || isLoading}
        className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {socialLoading === 'google' ? (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        ) : (
          <>
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </>
        )}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div>

      {/* Email/Password Form */}
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            required
            value={loginData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your email"
          />
        </div>

        <div>
          <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              required
              value={loginData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || socialLoading !== null}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            'Sign In'
          )}
        </button>
      </form>
    </motion.div>
  );

  const renderSignUpForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center w-full max-w-2xl">
          {[1, 2, 3, 4, 5].map((step) => (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step <= currentStep 
                    ? 'bg-blue-500 text-white shadow-md' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step < currentStep ? <Check className="w-5 h-5" /> : step}
                </div>
              </div>
              {step < 5 && (
                <div className={`flex-1 h-1 mx-2 transition-all ${
                  step < currentStep ? 'bg-blue-500' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <form onSubmit={handleRegistration} className="space-y-4">
        {renderStepContent()}

        {/* Error Message */}
        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-6">
          <button
            type="button"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </button>

          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <span>{currentStep === 5 ? 'Create Account' : 'Next'}</span>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-900">Choose Your Account Type</h3>
            
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                I want to register as: *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <motion.button
                  type="button"
                  onClick={() => handleInputChange('userType', 'customer')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-6 border-2 rounded-xl text-left transition-all ${
                    formData.userType === 'customer'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${
                      formData.userType === 'customer' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Users className={`w-8 h-8 ${
                        formData.userType === 'customer' ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className={`text-lg font-semibold mb-1 ${
                        formData.userType === 'customer' ? 'text-blue-700' : 'text-gray-700'
                      }`}>
                        Customer
                      </div>
                      <div className="text-sm text-gray-600">Order custom garments</div>
                    </div>
                    {formData.userType === 'customer' && (
                      <Check className="w-6 h-6 text-blue-500" />
                    )}
                  </div>
                </motion.button>
                
                <motion.button
                  type="button"
                  onClick={() => handleInputChange('userType', 'business')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-6 border-2 rounded-xl text-left transition-all ${
                    formData.userType === 'business'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${
                      formData.userType === 'business' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Scissors className={`w-8 h-8 ${
                        formData.userType === 'business' ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className={`text-lg font-semibold mb-1 ${
                        formData.userType === 'business' ? 'text-blue-700' : 'text-gray-700'
                      }`}>
                        Tailor/Business
                      </div>
                      <div className="text-sm text-gray-600">Provide tailoring services</div>
                    </div>
                    {formData.userType === 'business' && (
                      <Check className="w-6 h-6 text-blue-500" />
                    )}
                  </div>
                </motion.button>
              </div>
            </div>

            {/* Subscription Plan Selection for Business/Tailor */}
            {formData.userType === 'business' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6"
              >
                <SubscriptionPlanSelector
                  selectedPlan={formData.selectedPlan || 'free'}
                  onPlanSelect={(planId) => handleInputChange('selectedPlan', planId)}
                />
                {fieldErrors.selectedPlan && (
                  <p className="mt-2 text-sm text-red-600">{fieldErrors.selectedPlan}</p>
                )}
              </motion.div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your email"
                />
              </div>
              {emailValidation.isLoading && (
                <p className="text-blue-600 text-sm mt-1">Checking email availability...</p>
              )}
              {emailValidation.error && (
                <p className="text-red-600 text-sm mt-1">{emailValidation.error}</p>
              )}
              
              {/* Email OTP Verification (only for non-OAuth users) */}
              {!isOAuthFlow && (
                <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                  {!emailVerified ? (
                    <div className="space-y-3">
                      {!otpSent ? (
                        <button
                          type="button"
                          onClick={handleSendRegistrationOtp}
                          disabled={isSendingOtp || !formData.email || !formData.email.includes('@') || emailValidation.isLoading || emailValidation.isAvailable === false}
                          className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSendingOtp ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            'Send Verification Code'
                          )}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <label htmlFor="otp-code" className="block text-sm font-medium text-gray-700 mb-1">
                              Enter Verification Code
                            </label>
                            <input
                              id="otp-code"
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={otpCode}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                setOtpCode(value);
                                setOtpError(null);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                              placeholder="000000"
                            />
                            {otpExpiresAt && (
                              <p className="text-xs text-gray-500 mt-1 text-center">
                                Code expires at {new Date(otpExpiresAt).toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleVerifyRegistrationOtp}
                              disabled={isVerifyingOtp || otpCode.length !== 6}
                              className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isVerifyingOtp ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Verifying...
                                </>
                              ) : (
                                'Verify'
                              )}
                            </button>
                            
                            <button
                              type="button"
                              onClick={handleSendRegistrationOtp}
                              disabled={isSendingOtp || resendCooldown > 0}
                              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {isSendingOtp ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : resendCooldown > 0 ? (
                                `Resend in ${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, '0')}`
                              ) : (
                                'Resend'
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {otpError && (
                        <p className="text-red-600 text-sm">{otpError}</p>
                      )}
                      
                      {fieldErrors.emailVerification && (
                        <p className="text-red-600 text-sm">{fieldErrors.emailVerification}</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 text-green-600">
                      <Check className="w-5 h-5" />
                      <span className="text-sm font-medium">Email verified successfully</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isOAuthFlow && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => {
                    const newPassword = e.target.value;
                    handleInputChange('password', newPassword);
                    
                    // Real-time password validation for non-OAuth flows
                    if (!isOAuthFlow && newPassword) {
                      const passwordValidation = validatePassword(newPassword);
                      if (!passwordValidation.isValid) {
                        setFieldErrors(prev => ({
                          ...prev,
                          password: passwordValidation.error || 'Password does not meet requirements'
                        }));
                      } else {
                        // Clear password error if validation passes
                        setFieldErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.password;
                          return newErrors;
                        });
                      }
                    } else {
                      // Clear password error when user starts typing or for OAuth flows
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.password;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldErrors.password ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Always show password requirements during signup */}
              <div className="mt-2 text-xs text-gray-600">
                <p className="mb-1 font-medium">Password requirements:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li className={formData.password.length >= 8 ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(formData.password) ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.password) ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(formData.password) ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    One number
                  </li>
                  <li className={specialCharRegex.test(formData.password) ? 'text-green-600 font-medium' : 'text-gray-500'}>
                    One special character (!@#$%^&*(),.?":{}|&lt;&gt;)
                  </li>
                </ul>
              </div>
              {/* Show password validation error if present */}
              {fieldErrors.password && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
              )}
            </div>
            )}
            
            {isOAuthFlow && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 flex items-center">
                  <Info className="w-4 h-4 mr-2 flex-shrink-0" />
                  You're signing in with Google. No password is required.
                </p>
              </div>
            )}

            {!isOAuthFlow && (
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    handleInputChange('confirmPassword', e.target.value);
                    // Clear confirm password error when user starts typing
                    if (fieldErrors.confirmPassword) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.confirmPassword;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldErrors.confirmPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Show confirm password validation error if present */}
              {fieldErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.confirmPassword}</p>
              )}
            </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
            
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => {
                    handleInputChange('fullName', e.target.value);
                    // Clear name error when user starts typing
                    if (fieldErrors.fullName) {
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.fullName;
                        return newErrors;
                      });
                    }
                  }}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    fieldErrors.fullName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>
              {fieldErrors.fullName && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.fullName}</p>
              )}
            </div>

            <InternationalPhoneInput
              label="Phone Number"
              value={formData.phone}
              onChange={(phone, isValid) => {
                handleInputChange('phone', phone);
                if (!isValid && phone) {
                  setFieldErrors(prev => ({ ...prev, phone: 'Please enter a valid phone number' }));
                } else {
                  setFieldErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.phone;
                    return newErrors;
                  });
                }
              }}
              required
              error={fieldErrors.phone}
              defaultCountry="in"
            />

            {/* SMS Consent Checkbox */}
            <div className={`border rounded-lg p-4 ${
              isSMSVerificationRequired()
                ? 'bg-red-50 border-red-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="smsConsent"
                  checked={formData.smsConsent || false}
                  onChange={(e) => handleInputChange('smsConsent', e.target.checked)}
                  className={`mt-1 h-4 w-4 focus:ring-blue-500 border-gray-300 rounded ${
                    isSMSVerificationRequired()
                      ? 'text-red-600'
                      : 'text-blue-600'
                  }`}
                />
                <div className="flex-1">
                  <label htmlFor="smsConsent" className={`text-sm font-medium cursor-pointer ${
                    isSMSVerificationRequired() ? 'text-red-700' : 'text-gray-700'
                  }`}>
                    SMS Verification {isSMSVerificationRequired() ? '(Required)' : '(Optional)'}
                  </label>
                  <p className={`text-xs mt-1 ${
                    isSMSVerificationRequired() ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    I consent to receive SMS verification codes for account security.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {formData.userType === 'customer' ? 'Preferences' : 'Business Information'}
            </h3>
            
            {formData.userType === 'customer' ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Language
                  </label>
                  <select
                    id="language"
                    value={formData.preferences?.language || 'en'}
                    onChange={(e) => handleInputChange('preferences', {
                      ...formData.preferences,
                      language: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="en">English</option>
                    <option value="pa">Punjabi (ਪੰਜਾਬੀ)</option>
                    <option value="hi">Hindi (हिंदी)</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Notifications</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.preferences?.notifications || false}
                        onChange={(e) => handleInputChange('preferences', {
                          ...formData.preferences,
                          notifications: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Order updates and notifications</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.preferences?.marketing || false}
                        onChange={(e) => handleInputChange('preferences', {
                          ...formData.preferences,
                          marketing: e.target.checked
                        })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Marketing communications</span>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                    Business Name *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="businessName"
                      type="text"
                      required
                      value={formData.businessName || ''}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your business name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                    Business Type
                  </label>
                  <select
                    id="businessType"
                    value={formData.businessType || 'tailor'}
                    onChange={(e) => handleInputChange('businessType', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="tailor">Tailor</option>
                    <option value="fashion_house">Fashion House</option>
                    <option value="boutique">Boutique</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Review & Complete</h3>
            
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex items-center space-x-2">
                <User className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-700">
                  <strong>Name:</strong> {formData.fullName}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-700">
                  <strong>Email:</strong> {formData.email}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-5 h-5 text-gray-600" />
                <span className="text-sm text-gray-700">
                  <strong>Phone:</strong> {formData.phone}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {formData.userType === 'customer' ? (
                  <Users className="w-5 h-5 text-gray-600" />
                ) : (
                  <Scissors className="w-5 h-5 text-gray-600" />
                )}
                <span className="text-sm text-gray-700">
                  <strong>Account Type:</strong> {formData.userType === 'customer' ? 'Customer' : 'Business'}
                </span>
              </div>
              {formData.userType === 'business' && (
                <>
                  {formData.selectedPlan && (
                    <div className="flex items-center space-x-2">
                      <Crown className="w-5 h-5 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        <strong>Plan:</strong> {formData.selectedPlan.charAt(0).toUpperCase() + formData.selectedPlan.slice(1)}
                      </span>
                    </div>
                  )}
                  {formData.businessName && (
                    <div className="flex items-center space-x-2">
                      <Building className="w-5 h-5 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        <strong>Business:</strong> {formData.businessName}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="text-sm text-gray-600 space-y-3">
              <p>By creating an account, you agree to our:</p>
              <div className="flex flex-wrap gap-4 items-center">
                <Link 
                  to="/terms-of-service" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Terms of Service
                </Link>
                <span className="text-gray-400">|</span>
                <Link 
                  to="/privacy-policy" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navigation />
      <div className="flex flex-col items-center py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl w-full">
          {/* Logo at Top */}
          <div className="flex justify-center mb-8">
            <Logo size="3xl" />
          </div>

          {/* Heading */}
          <div className="text-center mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              {authMode === 'signin' ? 'Welcome Back' : 'Create Your Account'}
            </h1>
            <p className="text-lg text-gray-600">
              {authMode === 'signin' 
                ? 'Sign in to your MasterJi account' 
                : 'Join MasterJi and order custom garments'
              }
            </p>
          </div>

          {/* Auth Form - Full Width, Expanding Vertically */}
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            {/* Auth Mode Toggle */}
            <div className="flex mb-6 max-w-md mx-auto">
              <button
                onClick={() => {
                  navigate('/auth?mode=signin');
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-l-lg transition-colors ${
                  authMode === 'signin'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  navigate('/auth?mode=signup');
                }}
                className={`flex-1 py-2 px-4 text-sm font-medium rounded-r-lg transition-colors ${
                  authMode === 'signup'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Sign Up
              </button>
            </div>

            {/* Form Content */}
            <AnimatePresence mode="wait">
              {authMode === 'signin' ? renderSignInForm() : renderSignUpForm()}
            </AnimatePresence>

            {/* Switch Mode */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {authMode === 'signin' ? "Don't have an account?" : "Already have an account?"}{' '}
                <button
                  onClick={() => {
                    if (authMode === 'signin') {
                      // Navigate to signup to ensure proper flow with email verification
                      navigate('/auth?mode=signup');
                    } else {
                      // Navigate to signin
                      navigate('/auth?mode=signin');
                    }
                  }}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {authMode === 'signin' ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {verificationState.showPrompt && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 24 }}
            >
              <button
                type="button"
                onClick={handleDismissVerificationPrompt}
                className="absolute right-4 top-4 rounded-full bg-gray-100 p-2 text-gray-500 transition hover:bg-gray-200 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="space-y-5">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Secure your account</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    Verify either your email or phone number to unlock the full MasterJi experience. It only takes a few seconds.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => handleVerificationChannelChange('email')}
                    className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                      selectedVerificationChannel === 'email'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:text-blue-600'
                    }`}
                  >
                    Verify by Email
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerificationChannelChange('sms')}
                    disabled={isSMSChannelDisabled}
                    className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                      selectedVerificationChannel === 'sms'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 hover:text-blue-600'
                    } ${isSMSChannelDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                    title={isSMSChannelDisabled ? 'Add a phone number to your profile to use SMS verification.' : undefined}
                  >
                    Verify by SMS
                  </button>
                </div>

                {selectedVerificationChannel === 'sms' && isSMSChannelDisabled && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    Add a valid phone number in your profile settings to enable SMS verification.
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="verification-code" className="text-sm font-medium text-gray-700">
                      Verification code
                    </label>
                    {verificationState.lastSentTo && (
                      <span className="text-xs text-gray-500">
                        Sent to {getMaskedContact(selectedVerificationChannel)}
                      </span>
                    )}
                  </div>
                  <input
                    id="verification-code"
                    type="text"
                    inputMode="numeric"
                    value={verificationCodeInput}
                    onChange={(event) => setVerificationCodeInput(event.target.value.replace(/\s/g, ''))}
                    placeholder="Enter the 6-digit code"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  {verificationState.expiresAt && (
                    <p className="text-xs text-gray-500">
                      Code expires at {new Date(verificationState.expiresAt).toLocaleTimeString()}
                    </p>
                  )}
                </div>

                {(verificationState.error || verificationFeedback) && (
                  <div className="space-y-2">
                    {verificationState.error && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
                        {verificationState.error}
                      </div>
                    )}
                    {verificationFeedback && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700">
                        {verificationFeedback}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={handleSendVerificationCode}
                    disabled={verificationState.isSending || (selectedVerificationChannel === 'sms' && isSMSChannelDisabled)}
                    className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                  >
                    {verificationState.isSending && <Loader2 className="h-4 w-4 animate-spin" />}
                    {verificationState.isSending ? 'Sending...' : 'Send Code'}
                  </button>
                  <button
                    type="button"
                    onClick={handleVerifyCodeSubmit}
                    disabled={verificationState.isVerifying || verificationCodeInput.trim().length === 0}
                    className="flex items-center justify-center gap-2 rounded-lg border border-blue-600 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
                  >
                    {verificationState.isVerifying && <Loader2 className="h-4 w-4 animate-spin" />}
                    {verificationState.isVerifying ? 'Verifying...' : 'Verify'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleDismissVerificationPrompt}
                  className="text-sm font-medium text-gray-500 underline underline-offset-4 transition hover:text-gray-700"
                >
                  I&apos;ll do this later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UnifiedAuthPage;
